import { type FunctionComponent } from 'react';
import { formatDateToLocalString } from '../../../../utils/date/date.utils';
import {
	RequiredDateStatusTagEnum,
	RequiredDateStatusTagLines,
	RequiredDateStatusTagStatusColors
} from '../../../constants/digital-branch-order/digital-branch-order.constants';
import { isSevenDaysOrLessAway } from '../../../helpers/digital-branch-order/digital-branch-order.helper';
import { ClickableElement } from '../../buttons';
import CalendarAddOn from '../../svgs/calendar-add-on.svg';
import CalendarCheck from '../../svgs/calendar-check.svg';
import CalendarLock from '../../svgs/calendar-lock.svg';
import CalendarWarning from '../../svgs/calendar-warning.svg';

const StatusTagStatusIcons = {
	MISSING: <CalendarAddOn className="f6 theme-warning" />,
	UPCOMING: <CalendarWarning className="f6 theme-warning" />,
	PASSED: <CalendarWarning className="f6 theme-danger" />,
	APPLIED: <CalendarCheck className="f6 theme-info" />,
	LOCKED: <CalendarLock className="f6 theme-grey-dark" />
};

type RequiredDateStatusTagContentProps = {
	requiredDate?: Date;
	isLocked?: boolean;
};

const RequiredDateStatusTagContent: FunctionComponent<RequiredDateStatusTagContentProps> = ({ requiredDate, isLocked }) => {
	const currentDate = new Date();

	let status: RequiredDateStatusTagEnum = RequiredDateStatusTagEnum.APPLIED;
	if (isLocked) {
		status = RequiredDateStatusTagEnum.LOCKED;
	} else if (!requiredDate) {
		status = RequiredDateStatusTagEnum.MISSING;
	} else if (requiredDate < currentDate) {
		status = RequiredDateStatusTagEnum.PASSED;
	} else if (isSevenDaysOrLessAway(requiredDate)) {
		status = RequiredDateStatusTagEnum.UPCOMING;
	}

	return (
		<div
			className={`flex items-center gc1 pa1 bg-theme-${
				isLocked ? 'grey-lighter' : RequiredDateStatusTagStatusColors[status] + '-light'
			} theme-${RequiredDateStatusTagStatusColors[status]} br2`}
			style={{ width: 'fit-content' }}>
			{StatusTagStatusIcons[status]}
			<div className={`flex ${isLocked ? 'flex-row-reverse' : 'flex-row'} gc1 b lh-title f7`}>
				{requiredDate && (
					<span>
						{formatDateToLocalString(requiredDate, {
							month: '2-digit',
							day: '2-digit',
							year: '2-digit'
						})}
					</span>
				)}
				{RequiredDateStatusTagLines[status] && <span>{RequiredDateStatusTagLines[status]}</span>}
			</div>
		</div>
	);
};

export type RequiredDateStatusTagProps = {
	onClick?: () => void;
	requiredDate?: Date;
	isLocked?: boolean;
};

export const RequiredDateStatusTag: FunctionComponent<RequiredDateStatusTagProps> = ({ requiredDate, isLocked, onClick }) => {
	return isLocked ? (
		<RequiredDateStatusTagContent requiredDate={requiredDate} isLocked={isLocked} />
	) : (
		<ClickableElement ariaLabel="Required Date Status" onClick={onClick}>
			<RequiredDateStatusTagContent requiredDate={requiredDate} isLocked={isLocked} />
		</ClickableElement>
	);
};
